import { useIntl, FormattedMessage } from 'react-intl';
import {
  Form,
  Row,
  Col,
  FormInstance,
  Input,
  Divider,
  Tooltip,
  Button,
  Typography,
} from 'antd';

import { useState } from 'react';
import moment, { Moment } from 'moment';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import {
  DirectSale,
  DirectSaleFormValues,
  DirectSaleStatusEnum,
} from '@/core/domain/sales/DirectSale/DirectSale.types';
import {
  CompaniesInfiniteSelectField,
  CustomerInputSearch,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import DirectSalePartsTable from '../DirectSalePartsTable';
import SituationFormField from '../SituationFormField';
import Authorities from '@/types/Authority';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import { InvoiceDTO } from '@/core/domain/invoice/invoice/Invoice.types';
import { FaUserEdit, FaUserPlus } from 'react-icons/fa';
import CustomerSimplifiedRegisterModal from '@/components/Customers/CustomerSimplifiedRegisterModal';
import { useCustomerReducedQuery } from '@/core/domain/people/Customers/Customers.hooks';
import { Customer } from '@/core/domain/people/Customers/Customers.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useMatrixCustomerSettingsDetails } from '@/core/domain/enterprise/MatrixCustomerSettings/MatrixCustomerSettings.hooks';
import StatusTag from '@/components/_common/StatusTag';
import DirectSaleHeaderTag from '../DirectSaleHeaderTag';
import DirectSaleItemsDiscountModal from './DirectSaleItemsDiscountModal';

interface Props {
  form: FormInstance<DirectSaleFormValues>;
  directSale?: DirectSale;
  invoiceData?: InvoiceDTO[];
}

function DirectSaleForm({ form, directSale, invoiceData }: Props) {
  const intl = useIntl();
  const [customerModalOpen, customerModalActions] = useDisclosure();
  const [modalItemsDiscountVisible, modalItemsDiscountAction] =
    useDisclosure(false);

  const formItems = Form.useWatch('items', form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const { data: customerMatrixSettings } = useMatrixCustomerSettingsDetails();

  const [companyId, setCompanyId] = useState<number>();

  const saleDate = Form.useWatch('saleDate', form);
  const authorizationDate = Form.useWatch('authorizationDate', form);
  const watchCustomerId = Form.useWatch('customerId', form);

  const handleFormValuesChange = (valuesChanged: DirectSaleFormValues) => {
    handleCompanyChange(valuesChanged);
  };

  const handleCompanyChange = (valuesType: DirectSaleFormValues) => {
    if (!valuesType.companyId) return;

    setCompanyId(valuesType.companyId);
  };

  const { data: customerData, dataUpdatedAt } =
    useCustomerReducedQuery(watchCustomerId);

  const handleCustomerFinishCallback = (customer: Customer) => {
    form.setFieldsValue({
      customerId: customer.id,
      customerName: customer.name,
    });

    customerModalActions.close();
  };

  function validationDates(
    value: Moment,
    isSaleDate: boolean,
    dateValidation?: Moment,
  ) {
    if (value && dateValidation) {
      const isInvalidDate =
        (isSaleDate && value.isAfter(dateValidation)) ||
        (!isSaleDate && value.isBefore(dateValidation));

      if (isInvalidDate) {
        return Promise.reject(
          new Error(
            `Deve ser ${
              isSaleDate ? 'menor' : 'maior'
            } ou igual a ${dateValidation?.format('DD/MM/YYYY HH:mm')}`,
          ),
        );
      }
    }

    return Promise.resolve();
  }

  const isCanceled = directSale?.status === DirectSaleStatusEnum.CANCELED;

  const totalItens = directSale
    ? directSale?.items.reduce(
        (acc, curr) =>
          (acc +=
            (curr.quantity ?? 0) * (curr.unitPrice ?? 0) -
            (curr.discountCurrency ?? 0)),
        0,
      )
    : formItems?.reduce(
        (acc, curr) =>
          (acc +=
            (curr.totalStock ?? 0) * (curr.unitaryValue ?? 0) -
            (curr.discountCost ?? 0)),
        0,
      );

  const isPrinted = directSale?.status === DirectSaleStatusEnum.PRINTED;
  const isClosed = directSale?.status === DirectSaleStatusEnum.CANCELED;

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleFormValuesChange}
      >
        <Row gutter={[16, 9]}>
          <Col span={24}>
            <Row
              gutter={[8, 12]}
              align="middle"
              style={{ marginBottom: '24px' }}
            >
              <Col>
                <SituationFormField
                  initialValue={form.getFieldValue('situation')}
                />
              </Col>

              {!!directSale && (
                <Col>
                  <Typography.Title
                    level={4}
                    id="status"
                    style={{ marginBottom: 0 }}
                  >
                    <DirectSaleHeaderTag />
                  </Typography.Title>
                </Col>
              )}
            </Row>
          </Col>

          {directSale?.id && (
            <Col lg={3} span={24} md={7}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'directSale.form.details.saleCode',
                })}
                name="saleCode"
                rules={[defaultEmptyFieldError]}
              >
                <Input disabled />
              </FloatingFormItem>
            </Col>
          )}

          <Col lg={7} span={24} md={17}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField
                disabled={!!directSale?.id || isCanceled}
                queryEnable
              />
            </FloatingFormItem>
          </Col>

          <Col lg={7} span={24} md={12}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.form.saller',
              })}
              name="employeeId"
              rules={[defaultEmptyFieldError]}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{
                  companyId: companyId ?? directSale?.companyId,
                  estimator: true,
                }}
                disabled={isCanceled}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={7}>
            <Row gutter={16}>
              <Col span={21}>
                <Form.Item hidden name="customerId">
                  <Input />
                </Form.Item>

                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'release.form.customer',
                  })}
                  name="customerName"
                  rules={[defaultEmptyFieldError]}
                >
                  <CustomerInputSearch
                    addonAfter={
                      customerData?.category?.id &&
                      customerMatrixSettings?.showCategoryInDirectSale ? (
                        <StatusTag
                          color={customerData?.category.color}
                          label={customerData?.category.name}
                          title={customerData?.category.name}
                          style={{ width: 100, display: 'block' }}
                        />
                      ) : undefined
                    }
                    fieldNameId="customerId"
                    disabled
                    onCustomerSelect={(customer) =>
                      form.setFieldsValue({
                        customerId: customer.id,
                        customerName: customer.name,
                      })
                    }
                    buttonSearchDisabled={
                      directSale?.status === DirectSaleStatusEnum.AUTHORIZED ||
                      isCanceled
                    }
                  />
                </FloatingFormItem>
              </Col>

              <Col span={3} style={{ marginTop: '4px' }}>
                <Tooltip
                  title={intl.formatMessage({
                    id: `workOrders.domain.complements.${
                      customerData?.id ? 'customerEdit' : 'customerCreate'
                    }`,
                  })}
                >
                  <Button
                    size="large"
                    icon={customerData?.id ? <FaUserEdit /> : <FaUserPlus />}
                    disabled={
                      directSale?.status === DirectSaleStatusEnum.AUTHORIZED ||
                      isCanceled
                    }
                    onClick={customerModalActions.open}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Col>

          <Col xs={24} sm={24} md={12} lg={6} xl={6}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.form.dateOfSale',
              })}
              name="saleDate"
              rules={[
                defaultEmptyFieldError,
                {
                  validator: (_, value) =>
                    validationDates(value, true, authorizationDate),
                },
              ]}
              initialValue={moment()}
            >
              <InputDate
                disabled={
                  !hasPermissions([Authorities.MOVIMENTATION_SALE_EDIT_DATE]) ||
                  isCanceled
                }
                showTime
                disabledDate={
                  directSale?.id
                    ? (date) => date.isAfter(authorizationDate)
                    : undefined
                }
              />
            </FloatingFormItem>
          </Col>

          {directSale?.authorizationDate && (
            <Col xs={24} sm={24} md={12} lg={6} xl={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'directSale.form.details.authorizationDate',
                })}
                name="authorizationDate"
                rules={[
                  defaultEmptyFieldError,
                  {
                    validator: (_, value) =>
                      validationDates(value, false, saleDate),
                  },
                ]}
              >
                <InputDate
                  showTime
                  disabled={
                    !hasPermissions([
                      Authorities.MOVIMENTATION_SALE_EDIT_AUTHORIZATION_DATE,
                    ]) || isCanceled
                  }
                  disabledDate={(date) => date.isBefore(saleDate)}
                />
              </FloatingFormItem>
            </Col>
          )}

          {directSale?.cancellationDate && (
            <>
              <Col xs={24} sm={24} md={12} lg={6} xl={6}>
                <FloatingFormItem
                  label={intl.formatMessage({
                    id: 'directSale.form.details.cancellationDate',
                  })}
                  name="cancellationDate"
                >
                  <InputDate showTime disabled />
                </FloatingFormItem>
              </Col>

              {directSale.reasonCancellation && (
                <Col lg={8} span={24} md={24}>
                  <FloatingFormItem
                    label={intl.formatMessage({
                      id: 'directSale.form.details.reasonCancellation',
                    })}
                    name="reasonCancellation"
                  >
                    <Input disabled />
                  </FloatingFormItem>
                </Col>
              )}
            </>
          )}

          {hasPermissions([Authorities.MOVIMENTATION_SALE_ITEM_VIEW]) && (
            <>
              <Col lg={24} span={24}>
                <DirectSalePartsTable
                  totalItens={totalItens}
                  directSale={directSale}
                  form={form}
                  invoiceData={invoiceData}
                />
              </Col>

              <Divider />
            </>
          )}

          <Col span={24} md={12} lg={6}>
            <Typography.Paragraph strong style={{ marginBottom: '16px' }}>
              Valor total de desconto em peças
            </Typography.Paragraph>

            <Input.Group compact className="floating-form-item-group">
              <FloatingFormItem
                name="partDiscount"
                label="Valor (R$)"
                style={{ flex: '1' }}
              >
                <InputDecimal fractions={2} disabled />
              </FloatingFormItem>

              <Button
                type="primary"
                size="large"
                disabled={isPrinted || isClosed || !formItems?.length}
                onClick={modalItemsDiscountAction.open}
              >
                Editar
              </Button>
            </Input.Group>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="note"
              label={<FormattedMessage id="directSale.form.observation" />}
            >
              <Input.TextArea rows={4} disabled={isCanceled} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>

      <CustomerSimplifiedRegisterModal
        visible={customerModalOpen}
        handleVisible={customerModalActions.close}
        customerId={customerData?.id}
        onFinishCallback={handleCustomerFinishCallback}
        key={dataUpdatedAt}
      />

      <DirectSaleItemsDiscountModal
        totalItens={totalItens}
        directSaleId={directSale?.id}
        open={modalItemsDiscountVisible}
        onCancel={modalItemsDiscountAction.close}
      />
    </>
  );
}

export default DirectSaleForm;
